<template>
  <div class="space-info" :style="panelStyles">
    <div v-if="spaceCoverImage" class="cover-image">
      <img :src="spaceCoverImage" alt="Space Cover Image" />
    </div>
    <SpaceLogo v-if="spaceCoverImage && spaceLogo" class="cover-logo" :logo="spaceLogo" :title="spaceName" diameter="120px"/>

    <SidebarHeader
      class="space-info-header"
      :menuItems="[
      {
        icon: 'edit',
        text: 'Customise',
        show: this.userLevel > 0,
        click: this.customisePortal
        },
        {
          icon: 'delete',
          show: userLevel > 0,
          text: 'Delete Portal',
          style: {color: 'var(--not-active-user)'},
          click: () => deleteRoom(data)
        }
        ]"
        >
        <template #title>
          {{spaceName}}
          <h4 class="space-info-header__company">
            <span v-if="companyName">{{companyName}} </span>
            <span class="space-type">
              <i>{{isOpenSpace ? 'Open Space' : 'Private Space'}}</i>
            </span>
          </h4>
        </template>
      </SidebarHeader>

    <div class="space-info-container">
      <div class="space-info-info" v-if="infoHeading || infoDescription">
        <p><b>{{infoHeading}}</b></p>
        <p>{{infoDescription}}</p>
      </div>

      <button class="space-info-join" @click="() => handleEnterSpacePortal(roomId)" v-if="hasAccess">
        <img
        class="space-info-join__icon"
        :src="require('@/assets/global-icons/jump-space.svg')"
        />
        Jump to {{spaceName}}
      </button>
      <button class="space-info-no-access" v-else>
        <img
        class="space-info-no-access__icon"
        :src="require('@/assets/global-icons/eye-show.svg')"
        />
        <div class="space-info-no-access__text">
          <b>Preview Only</b>
          <span>You are not a member of {{spaceName}} Space.</span>
        </div>
      </button>

      <div class="space-info-box space-info-number">
        <img src="@/assets/global-icons/user-circle.svg"/>
        <b>{{usersOnline}} Online</b>
      </div>

      <div class="space-info-participants" v-if="hasAccess">
        <SearchResultUser
          v-for="user in spaceParticipants"
          :key="user.id"
          type="portal-info"
          :data="user"
          :showStatus="false"
          :isSpaceAdmin="data.isSpaceAdmin"
          :isInJumpSpace="true"
          :spaceInfo="data"
          />
          <template v-if="isLoadingParticipants">
            <Skeleton
            v-for="i in 3" :key="i+spaceParticipants.length"
            size="100% auto" padding="12px 12px 12px 63px" rounded gradient noverflow shadow-light
            >
            <Skeleton size="45px" circle shimmer absolute offsets="10px auto auto 10px"/>
            <Skeleton size="75% 17px" margin="0 0 9px" shimmer rounded-full/>
            <Skeleton size="60% 12px" margin="4px 0" shimmer rounded-full/>
            <Skeleton size="40% 12px" margin="4px 0" shimmer rounded-full/>
          </Skeleton>
        </template>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SidebarHeader from '../SidebarHeader.vue';
import SearchResultUser from '@/components/search/SearchResultUser.vue';
import Skeleton from '@/components/global/loading/Skeleton.vue'
import CustomiseRoomModal from '@/components/modals/nav/CustomiseRoomModal.vue';
import SpaceLogo from '@/components/atoms/SpaceLogo.vue';

export default {
  name: 'JumpSpaceInfo',
  components: { SearchResultUser, Skeleton, SidebarHeader, SpaceLogo },
  computed: {
    ...mapGetters({
      data: 'getCurrentSpacePageData',
      user: 'current_user/data',
      userLevel: 'current_user/currentAreaPermissionNo',
      isAuthorisedLocation: 'isAuthorisedLocation',
    }),
    ...mapGetters('search', [
      'ifLoadingParticipants',
      'spaceParticipants',
      'isLoadingMoreParticipants',
      'isThereMoreParticipants',
    ]),
    panelStyles() {
      let styles = {
        'grid-template-rows': `${this.spaceCoverImage ? '180px ' : ''}${this.spaceLogo && this.spaceCoverImage ? '45px ' : ''}auto 3fr`
      }
      return styles
    },
    roomId() {
      return this.data?.id || ''
    },
    referenceLocationData() {
      return this.data?.referenceLocation || {}
    },
    spaceName() {
      return this.referenceLocationData?.name || ''
    },
    spaceCoverImage() {
      return this.referenceLocationData?.info?.coverImage || ''
    },
    spaceLogo() {
      return this.referenceLocationData?.logo || ''
    },
    infoHeading() {
      return this.referenceLocationData?.info?.heading || 'Welcome!'
    },
    infoDescription() {
      return this.referenceLocationData?.info?.description || ''
    },
    companyName() {
      return this.referenceLocationData?.companyName || ''
    },
    isOpenSpace() {
      return this.referenceLocationData?.areaType === 'open' || false
    },
    usersOnline() {
      return this.data?.location?.online || 0
    },
    isLoadingParticipants: vm => vm.ifLoadingParticipants || vm.isLoadingMoreParticipants,
    referenceId: vm => vm.data?.location?.reference || '',
    hasAccess: vm => vm.isAuthorisedLocation(vm.roomId),
  },
  methods: {
    ...mapActions(['routing/goToCustomise', 'sidebar/close', 'deleteRoom', 'stopViewingRoom']),
    ...mapActions('guest/space', ['handleAddOpenSpaceToCollectionAndRegister']),
    ...mapActions('search', ['fetchCurrentSpaceParticipants', 'fetchMoreSpaceParticipants']),
    ...mapActions(['handleEnterSpacePortal', 'modal/open']),
    onScroll(e) {
      const el = e.target
      const scrollY = el.clientHeight + el.scrollTop
      const distanceFromBottom = el.scrollHeight - scrollY

      if (distanceFromBottom < 100 && this.isThereMoreParticipants) {
        this.fetchMoreSpaceParticipants()
      }
    },
    customisePortal() {
      this['modal/open']([CustomiseRoomModal]);
    }
  },
  mounted(){
    if(this.hasAccess) this.fetchCurrentSpaceParticipants({portal: true, id: this.referenceId})
  },
  destroyed(){
    this.stopViewingRoom();
  }
}
</script>

<style scoped lang="scss">
.space-info {
  color: #B3B3B3;
  line-height: 1;
  display: grid;
  flex-flow: column;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;

  b {
    color: white;
  }

  & .cover-image {
    height: 180px;
    overflow: hidden;
    margin: -20px;
    margin-bottom: 1rem;

    & img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  & .cover-logo {
    margin-top: -95px;
  }

  &-header {
    box-shadow: none !important;
    padding: 0 !important;
    margin-bottom: 30px;

    &__company {
      margin-top: 2px;
      font-size: 0.9rem;

      & .space-type {
        color: #B3B3B3;
        font-weight: normal;
      }
    }
  }

  &-box {
    margin-bottom: 20px;
    background-color: $grey-6;
    padding: 20px;
    border-radius: 1000rem;
  }

  &-container {
    display: grid;
    align-content: flex-start;
    max-height: 100%;
    overflow-y: auto;
    padding-right: 20px;
    margin-right: -20px;
  }

  &-info {
    line-height: 1.2;
    color: white;
    display: grid;
    gap: 1rem;
  }

  &-join {
    border: none;
    text-align: left;
    background-color: #3bc146;
    display: flex;
    align-items: center;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
    border-radius: 100px;
    cursor: pointer;
    padding: 7px 20px;
    padding-left: 12px;
    color: white;
    font-weight: bold;
    margin-top: 3.5rem;
    margin-bottom: 1.5rem;

    &__icon {
      margin-right: 0.6rem;
      height: 27px;
      opacity: 0.6;
      transition: opacity 0.125s;
    }

    &:hover &__icon {
      opacity: 1;
    }
  }
  &-no-access {
    border: none;
    text-align: left;
    background-color: $grey-6;
    display: flex;
    align-items: flex-start;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
    border-radius: 17px;
    color: white;
    margin-top: 3.5rem;
    margin-bottom: 1.5rem;
    gap: 1rem;
    padding: 12px 20px;
    padding-left: 12px;
    font-size: 14px;

    &__icon {
      width: 22px;
      margin-top: 1px;
    }
    &__text {
      display: flex;
      flex-flow: column;
      gap: 5px;
    }
    & span {
      color: $grey-2;
    }
  }

  &-number {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
    padding: 12px 20px;
    padding-left: 12px;
    font-size: 14px;
  }

  &-help, .become-member {
    text-decoration: none;
    display: flex;
    justify-content: space-between;
    color: #B3B3B3;
    align-items: center;
    line-height: 1.4;
    cursor: pointer;
  }

  &-participants {
    display: flex;
    flex-flow: column;
    gap: 10px;
    margin-top: 20px;
  }

  &__copyright {
    font-size: 13px;
    // parent is set to display: flex to get this to work
    margin-top: 1rem;
    align-self: flex-end;
  }
}
</style>
